// src/encounter/services/use-clinical-edits.ts
// POST /api/Patients/RunClinicalEdits/{encounterId} → ClinicalEditData[]
// The rule engine evaluates the encounter server-side (the route takes only the
// encounterId — parity with Flutter FetchClinicalEditUseCase(encounterId)) and
// returns the clinical-edit suggestions. Verified routed: 200 + [] on dev-api.
import { useQuery } from '@tanstack/react-query';
import { post, ApiError } from '@/shared/api/api-client';
import type { ClinicalEditData } from '@/types/clinical-edit-api';

/**
 * Retry policy for the clinical-edit query. A 503 is the engine's intentional
 * "checks unavailable" signal (PROD-1303), not a transient fault — surface the
 * degraded state immediately instead of sitting on the default 3-retry backoff
 * (~4-12s spinner). Every other failure keeps the normal 3 retries.
 */
export function shouldRetryClinicalEdits(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status === 503) return false;
  return failureCount < 3;
}

export function useClinicalEdits(encounterId: string | null | undefined) {
  return useQuery<ClinicalEditData[]>({
    queryKey: ['clinical-edits', encounterId],
    enabled: !!encounterId,
    // The rule engine re-evaluates server-side; reopening the drawer Edit tab
    // within the window reuses cache instead of re-firing the eval on every mount.
    staleTime: 30_000,
    retry: shouldRetryClinicalEdits,
    queryFn: ({ signal }) =>
      post<ClinicalEditData[]>(
        `/api/Patients/RunClinicalEdits/${encodeURIComponent(encounterId as string)}`,
        {},
        { signal },
      ),
  });
}
